import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { warehouseApi, locationApi } from '../services/inventoryApi';
import type {
  WarehouseCreate,
  WarehouseUpdate,
  LocationCreate,
  LocationUpdate,
  LocationBulkStatusRequest,
  LocationBulkDeleteRequest,
  ListWarehousesParams,
} from '../types';

export const warehouseKeys = {
  all: ['warehouses'] as const,
  list: (params?: ListWarehousesParams) => [...warehouseKeys.all, 'list', params] as const,
  detail: (id: string) => [...warehouseKeys.all, 'detail', id] as const,
  locations: (warehouseId: string) => [...warehouseKeys.all, 'locations', warehouseId] as const,
  location: (locationId: string) => [...warehouseKeys.all, 'location', locationId] as const,
  locationStock: (locationId: string) =>
    [...warehouseKeys.all, 'location', locationId, 'stock'] as const,
};

export function useWarehouses(params?: ListWarehousesParams) {
  return useQuery({
    queryKey: warehouseKeys.list(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => warehouseApi.list(params),
  });
}

export function useWarehouse(id: string) {
  return useQuery({
    queryKey: warehouseKeys.detail(id),
    queryFn: () => warehouseApi.get(id),
    enabled: !!id,
  });
}

export function useCreateWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WarehouseCreate) => warehouseApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
    },
  });
}

export function useUpdateWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WarehouseUpdate }) => warehouseApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
    },
  });
}

export function useDeleteWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => warehouseApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
    },
  });
}

// Location hooks
export function useLocations(warehouseId: string) {
  return useQuery({
    queryKey: warehouseKeys.locations(warehouseId),
    queryFn: () => locationApi.list(warehouseId),
    enabled: !!warehouseId,
  });
}

export function useLocation(locationId: string) {
  return useQuery({
    queryKey: warehouseKeys.location(locationId),
    queryFn: () => locationApi.get(locationId),
    enabled: !!locationId,
  });
}

/** Inventory held in a location and everything nested under it (recursive). */
export function useLocationStock(locationId: string) {
  return useQuery({
    queryKey: warehouseKeys.locationStock(locationId),
    queryFn: () => locationApi.getStock(locationId),
    enabled: !!locationId,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseId, data }: { warehouseId: string; data: LocationCreate }) =>
      locationApi.create(warehouseId, data),
    onSuccess: (_, { warehouseId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.locations(warehouseId) });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, warehouseId: _warehouseId }: { id: string; data: LocationUpdate; warehouseId: string }) =>
      locationApi.update(id, data),
    onSuccess: (updated, { warehouseId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.locations(warehouseId) });
      queryClient.invalidateQueries({ queryKey: warehouseKeys.location(updated.id) });
      // A renamed location shows in its (and ancestors') recursive stock panels.
      queryClient.invalidateQueries({ queryKey: [...warehouseKeys.all, 'location'] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; warehouseId: string }) => locationApi.delete(id),
    onSuccess: (_, { warehouseId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.locations(warehouseId) });
    },
  });
}

export function useBulkSetLocationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseId, data }: { warehouseId: string; data: LocationBulkStatusRequest }) =>
      locationApi.bulkSetStatus(warehouseId, data),
    onSuccess: (_, { warehouseId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.locations(warehouseId) });
    },
  });
}

export function useBulkDeleteLocations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseId, data }: { warehouseId: string; data: LocationBulkDeleteRequest }) =>
      locationApi.bulkDelete(warehouseId, data),
    onSuccess: (_, { warehouseId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.locations(warehouseId) });
    },
  });
}
